import type { AICreditStatus } from '@/lib/api/ai'
import { cn } from '@/lib/utils'
import { CoinsIcon, WarningCircleIcon } from '@phosphor-icons/react'

export function AICreditNotice({
  status,
  className,
  compact = false,
  tone = 'default',
}: {
  status?: AICreditStatus
  className?: string
  compact?: boolean
  tone?: 'default' | 'inverse'
}) {
  if (!status) return null

  const exhausted = !status.canUse
  const isFree = status.requiredCredits === 0
  const low =
    !isFree &&
    !exhausted &&
    status.remainingCredits <=
      Math.max(5, Math.ceil((status.monthlyCredits ?? 0) * 0.1))
  const Icon = exhausted ? WarningCircleIcon : CoinsIcon
  const usage = `${status.requiredCredits} AI credit${status.requiredCredits === 1 ? '' : 's'}`

  return (
    <div
      role={exhausted ? 'alert' : 'status'}
      className={cn(
        'flex items-start gap-2 rounded-md border px-2.5 py-2 text-xs',
        exhausted
          ? tone === 'inverse'
            ? 'border-rose-400/30 bg-rose-400/10 text-rose-300'
            : 'border-destructive/30 bg-destructive/5 text-destructive'
          : low
            ? tone === 'inverse'
              ? 'border-amber-400/30 bg-amber-400/10 text-amber-200'
              : 'border-amber-500/30 bg-amber-500/5 text-amber-700 dark:text-amber-300'
            : tone === 'inverse'
              ? 'border-slate-700 bg-slate-900/60 text-slate-400'
              : 'border-border/60 bg-muted/30 text-muted-foreground',
        compact && 'border-0 bg-transparent px-0 py-0',
        className,
      )}
    >
      <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0" weight="fill" />
      <span>
        {isFree
          ? 'Free · Benny chat does not use AI credits'
          : exhausted
            ? status.configured
              ? `Not enough AI credits. This uses ${usage}; ${status.remainingCredits} remain. Ask a workspace owner to add credits.`
              : 'AI credits are not configured. Ask a workspace owner to add credits.'
            : `${usage} per request · ${status.remainingCredits} remaining`}
      </span>
    </div>
  )
}
